import { getBlockFromModelId } from '../../../../utils/BlockLayout';
import message from '../../../../helper/message';
import { getNodeInfo } from '../../../../helper/config';

export const singleValueAttributeUpdater = async (props, value) => {
  const {
    attributeName,
    configuration,
    appApi,
    getAppState,
    setAppState,
    getDependOnBlock,
    removeModel,
    importModel,
    updateState,
  } = props;

  if (configuration[attributeName].value === value) return;

  const { modelId } = configuration[attributeName];
  const blocks = appApi.config.getAttributeDependOnBlocks(
    attributeName,
    configuration
  );
  let newAppState = getAppState();
  // block segment
  let block =
    typeof getDependOnBlock === 'function'
      ? getDependOnBlock(value, blocks)
      : attributeGetDependOnBlock(blocks, modelId);

  // removeModel segment
  newAppState =
    typeof removeModel === 'function'
      ? removeModel(block, newAppState)
      : singleValueAttributeRemoveModel(props, modelId, block, newAppState);

  // importModel segment
  block = block || (blocks && blocks[0]);
  setAppState({ disabled: true });
  const importState = await (typeof importModel === 'function'
    ? importModel(value, block)
    : singleValueAttributeImportModel(props, value, block));
  if (!importState) return singleValueAttributeCleanState(props, newAppState);

  // updateState segment
  newAppState =
    typeof updateState === 'function'
      ? updateState(value, importState, newAppState)
      : singleValueAttributeUpdateState(props, value, importState, newAppState);
  setAppState(newAppState);
};

function attributeGetDependOnBlock(blocks, modelId) {
  return blocks && getBlockFromModelId(blocks, modelId);
}

function singleValueAttributeRemoveModel(props, modelId, block, newAppState) {
  if (!modelId) return newAppState;
  const { actions } = props;
  block && block.unoccupyBlocks(modelId);
  return actions.removeModel(modelId, newAppState);
}

async function singleValueAttributeImportModel(props, value, block) {
  const { appApi, attributeName, configuration } = props;
  const { hasScene, blockDependOn } = appApi.config.getAttribute(attributeName);
  if (!hasScene || value === 'None') return;
  if (!block)
    throw new Error(`Attribute ${attributeName} missing dependOn block!`);
  const product = appApi.config.getProduct(value);
  const nodeInfo = getNodeInfo(product);
  const insertion = block.determineInsertion(nodeInfo);
  if (!insertion.isInsertionPossible)
    return message.error(
      `Fail to insert ${attributeName}, please check either the dimension  or the compatibility of product ${value}!`
    );

  const transform = block.getInsertTransform(insertion);
  const parent = configuration[blockDependOn[0][0]].modelId;
  const blocksData = appApi.config.getProductBlockData(attributeName, value);
  const importOptions = { blocksData, parent, transform };
  const importState = await appApi.model.importModel(
    product.sceneId,
    importOptions
  );
  block.occupyBlocks(importState.modelId, insertion);
  return importState;
}

function singleValueAttributeUpdateState(
  props,
  value,
  importState,
  newAppState
) {
  const { appApi, actions, configuration, attributeName, activeTab } = props;
  const { blockDependOn } = appApi.config.getAttribute(attributeName);

  let parent;
  try {
    parent = configuration[blockDependOn[0][0]].modelId;
  } catch (e) {
    throw new Error(`Can't get parent nodeId for attribute ${attributeName}!`);
  }

  newAppState = actions.addModel(
    importState,
    { tab: activeTab, attribute: attributeName, value, parent },
    newAppState
  );
  return actions.setConfiguration(
    {
      attribute: attributeName,
      value: { ...importState, value },
    },
    newAppState
  );
}

function singleValueAttributeCleanState(props, newAppState) {
  const { actions, setAppState, attributeName } = props;
  setAppState(
    actions.setConfiguration(
      {
        attribute: attributeName,
        value: { value: 'None' },
      },
      newAppState
    )
  );
}

export const multiValueAttributeUpdater = async (props, value, change) => {
  const {
    attributeName,
    appApi,
    configuration,
    getAppState,
    setAppState,
    getDependOnBlock,
    removeModel,
    importModel,
    updateState,
  } = props;

  if (change !== 1 && change !== -1)
    return message.error(
      `Unknow parameter change for multiValue attribute ${attributeName}! Change should be either 1 or -1.`
    );

  let newAppState = getAppState();
  const blocks = appApi.config.getAttributeDependOnBlocks(
    attributeName,
    configuration
  );
  const { hasScene } = appApi.config.getAttribute(attributeName);

  if (!hasScene || !blocks)
    return message.error(
      `List attribute ${attributeName} must has sceneId and has blocks to depend on!`
    );

  const modelId =
    change === -1 &&
    configuration[attributeName][value][
      configuration[attributeName][value].length - 1
    ].modelId;

  // block segment
  let block =
    typeof getDependOnBlock === 'function'
      ? getDependOnBlock(value, blocks)
      : attributeGetDependOnBlock(blocks, modelId);

  // removeModel segment
  if (change === -1) {
    newAppState =
      typeof removeModel === 'function'
        ? removeModel(block, newAppState)
        : multiValueAttributeRemoveModel(
            props,
            value,
            modelId,
            block,
            newAppState
          );
    return setAppState(newAppState);
  }

  // importModel segment
  block = block || blocks[0];
  setAppState({ disabled: true });
  const importState = await (typeof importModel === 'function'
    ? importModel(value, block)
    : multiValueAttributeImportModel(props, value, block));
  if (!importState) return setAppState({ disabled: false });

  // updateState segment
  newAppState =
    typeof updateState === 'function'
      ? updateState(value, importState, newAppState)
      : multiValueAttributeUpdateState(props, value, importState, newAppState);
  setAppState(newAppState);
};

async function multiValueAttributeImportModel(props, value, block) {
  const { appApi, configuration, attributeName, models } = props;
  const product = appApi.config.getProduct(value);
  const nodeInfo = getNodeInfo(product);
  const overlayCheck = appApi.config.getSpecialOverlayValidationData(
    value,
    configuration,
    models
  );
  const insertion = block.determineInsertion(nodeInfo, { overlayCheck });
  if (!insertion.isInsertionPossible)
    return message.warn(
      `Fail to insert ${attributeName}, the block doesn't have enough space.`
    );
  const { blockDependOn } = appApi.config.getAttribute(attributeName);
  const blocksData = appApi.config.getProductBlockData(attributeName, value);
  const transform = block.getInsertTransform(insertion);
  const parent = configuration[blockDependOn[0][0]].modelId;
  if (!parent)
    return message.error(
      'List attribute item must added to a single valued attribute'
    );
  const importOptions = { blocksData, parent, transform };
  const importState = await appApi.model.importModel(
    product.sceneId,
    importOptions
  );
  block.occupyBlocks(importState.modelId, insertion);
  return importState;
}

function multiValueAttributeRemoveModel(
  props,
  value,
  modelId,
  block,
  newAppState
) {
  const { actions, attributeName } = props;
  block.unoccupyBlocks(modelId);
  newAppState = actions.removeModel(modelId, newAppState);
  return actions.setConfiguration(
    {
      attribute: attributeName,
      value,
      change: -1,
    },
    newAppState
  );
}

function multiValueAttributeUpdateState(
  props,
  value,
  importState,
  newAppState
) {
  const { actions, appApi, activeTab, configuration, attributeName } = props;
  const { blockDependOn } = appApi.config.getAttribute(attributeName);
  const parent = configuration[blockDependOn[0][0]].modelId;
  newAppState = actions.addModel(
    importState,
    { tab: activeTab, attribute: attributeName, value, parent },
    newAppState
  );
  return actions.setConfiguration(
    {
      attribute: attributeName,
      value,
      change: importState,
    },
    newAppState
  );
}



// WEBPACK FOOTER //
// ./src/components/Attributes/standardAttributes/utils/handler.js